'use client';

import {
  PropsWithChildren,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useMediaQuery } from "@mui/material";
import { useQuery } from "@tanstack/react-query";

import dayjs from "@/common/lib/dayjs";

import { Breakpoints } from "@/common/theme/constants";

import { QUERY_KEYS } from "@/common/types/react-query-keys.interfaces";

import fetchFilterValuesBasedOnCategoryId, { FetchFilterValuesBasedOnCategoryIdReturnValue } from "@/common/components/Filter/util/fetchFilterValuesBasedOnCategoryId";
import useFilter from "@/common/components/Filter/util/useFilter";
import { Filter, FilterValue } from "@/generated/contentfulTypes";

import {
  DATE_PICKER_DESKTOP_DAYS,
  DATE_PICKER_MOBILE_DAYS,
  MAX_SCHEDULE_FUTURE_DAYS,
  SCHEDULE_HOUR_FORMAT,
  SCHEDULE_TIME_SLOTS,
} from "../classfinder.constants";
import organiseSchedules from "../utils/organiseSchedules";

import useClubData from "../hooks/useClubData";
import { useSchedules } from "../hooks/useSchedules";

import {
  CLASSFINDER_ENTITY_TYPES,
  ClassSchedule,
  FilterVariant,
  OrganisedSchedules,
  Schedule,
  ServiceSchedule,
} from "../classfinder.interfaces";

import {
  ClassfinderConfig,
  ClassfinderContextType,
} from "./Classfinder.context.interfaces";

type ClassfinderProviderProps = PropsWithChildren & {
  children: ReactNode;
  config: ClassfinderConfig;
}

const ClassfinderContext = createContext<ClassfinderContextType | undefined>(
  undefined
);

export function ClassfinderProvider({
  children,
  config,
}: ClassfinderProviderProps) {
  const isDesktop = useMediaQuery(`(min-width: ${Breakpoints.md}px)`);

  const { studioId, lang, type, showFilter, enabledFilters, filterTranslations } = config;

  const [isLoading, setIsLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(null);
  const [highlightedDate, setHighlightedDate] = useState<dayjs.Dayjs | null>(
    null
  );
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [selectedClass, setSelectedClass] = useState<Schedule | null>(null);
  const [selectedClassFilters, setSelectedClassFilters] = useState<string[]>(
    []
  );

  const {
    schedules,
    currentStartDate,
    currentEndDate,
    isFetchingSchedules,
    handleLoadMore,
    fetchSchedulesForDate,
  } = useSchedules({
    studioId,
    lang,
    type
  });

  const { clubData } = useClubData({
    studioId,
    lang
  });

  const filterValues: FilterValue[] = (
    clubData?.filterValuesCollection?.items ?? []
  ).filter((value): value is FilterValue => value !== null);

  const { data: allFilters } = useQuery({
    queryKey: [QUERY_KEYS.FILTER_VALUES, filterValues],
    queryFn: () =>
      fetchFilterValuesBasedOnCategoryId(filterValues as FilterValue[]),
    enabled: filterValues.length > 0 && showFilter !== false,
  });

  const filters = useMemo<FetchFilterValuesBasedOnCategoryIdReturnValue[] | undefined>(() => {
    if (!allFilters || showFilter === false) return undefined;

    if (!enabledFilters || enabledFilters.length === 0) {
      return allFilters;
    }

    const enabledFilterIds = enabledFilters
      .filter((filter): filter is Filter => filter !== null)
      .map((filter) => filter.sys.id);

    return allFilters.filter((filter) =>
      enabledFilterIds.includes(filter.category.sys.id)
    );
  }, [allFilters, enabledFilters, showFilter]);

  const transformSchedules = useMemo((): OrganisedSchedules => {
    const effectiveSelectedFilters =
      showFilter === false ? [] : selectedClassFilters;

    if (type === CLASSFINDER_ENTITY_TYPES.CLASSES) {
      const classSchedules = schedules.filter(
        (s): s is ClassSchedule => 'class' in s
      );

      return organiseSchedules({
        schedules: classSchedules,
        selectedTime,
        selectedDate,
        selectedClassFilters: effectiveSelectedFilters,
        currentEndDate
      });
    } else {
      const serviceSchedules = schedules.filter(
        (s): s is ServiceSchedule => 'service' in s
      );

      return organiseSchedules({
        schedules: serviceSchedules,
        selectedTime,
        selectedDate,
        selectedClassFilters: effectiveSelectedFilters,
        currentEndDate
      });
    }
  }, [
    schedules,
    selectedTime,
    selectedDate,
    selectedClassFilters,
    showFilter,
    type,
    currentEndDate,
  ]);

  const totalAmountOfClasses = useMemo(() => {
    return Object.entries(transformSchedules.filtered).reduce((acc, day) => {
      return acc + day[1].schedules.length;
    }, 0);
  }, [transformSchedules.filtered]);

  const { filterComponent } = useFilter({
    filterTranslations,
    totalAmountOfFilteredItems: totalAmountOfClasses,
    filters: filters ?? [],
    selectedFilters: selectedClassFilters,
    setSelectedFilters: setSelectedClassFilters,
    variant: FilterVariant.Classes,
  });

  // Context Helpers
  const initDayjsLocale = () => {
    const locale = lang.startsWith('nl') ? 'nl' : 'en';
    dayjs.locale(locale);
  };

  const timeSlots = Array.from({ length: SCHEDULE_TIME_SLOTS }, (_, i) =>
    dayjs().hour(i).minute(0).format(SCHEDULE_HOUR_FORMAT)
  );

  const visibleDays = isDesktop
    ? DATE_PICKER_DESKTOP_DAYS
    : DATE_PICKER_MOBILE_DAYS;

  const daysToShow = useMemo(() => {
    if (!selectedDate) return [];
    return Array.from({ length: visibleDays }, (_, i) =>
      dayjs().startOf('day').add(i, 'day')
    );
  }, [visibleDays, selectedDate]);

  const isDateDisabled = useCallback(
    (date: dayjs.Dayjs) =>
      date.isAfter(dayjs().add(MAX_SCHEDULE_FUTURE_DAYS, 'day'), 'day'),
    []
  );

  const handleTimeSelect = useCallback(
    (time: string | null) => {
      setSelectedTime(time);
      if (!selectedDate && time) {
        setSelectedDate(dayjs().startOf('day'));
      }
    },
    [selectedDate, selectedTime, setSelectedTime, setSelectedDate]
  );

  const handleDateSelect = useCallback(
    (date: dayjs.Dayjs) => {
      setSelectedDate(date);

      if (date.isBefore(currentStartDate) || date.isAfter(currentEndDate)) {
        fetchSchedulesForDate(date);
      }
    },
    [selectedDate, currentStartDate, currentEndDate, fetchSchedulesForDate]
  );

  const handleHighlightDate = useCallback(
    (date: dayjs.Dayjs | null) => {
      setHighlightedDate(date);
    },
    [setHighlightedDate]
  );

  const canLoadMore = useMemo(() => {
    return currentEndDate.isBefore(
      dayjs()
        .startOf('day')
        .add(MAX_SCHEDULE_FUTURE_DAYS - 1, 'day'),
      'day'
    );
  }, [currentEndDate]);

  const value = {
    config,
    clubData,
    loading: {
      isLoading,
      setIsLoading,
    },
    selectedClass: {
      selectedClass,
      setSelectedClass,
    },
    schedules: {
      total: totalAmountOfClasses,
      schedules: transformSchedules,
      currentStartDate,
      currentEndDate,
      isFetchingSchedules,
      handleLoadMore,
      canLoadMore,
    },
    datepicker: {
      selectedDate,
      highlightedDate,
      isDateDisabled,
      visibleDays,
      daysToShow,
      handleDateSelect,
      handleHighlightDate,
    },
    filter: {
      filters,
      filterComponent,
      selectedClassFilters,
      setSelectedClassFilters,
      showFilter
    },
    timeslot: {
      timeSlots,
      selectedTime,
      handleTimeSelect,
      canScrollLeft,
      canScrollRight,
      setCanScrollLeft,
      setCanScrollRight,
    },
  };

  useEffect(() => {
    initDayjsLocale();
    if (selectedDate === null) {
      setSelectedDate(dayjs().startOf('day'));
    }
  }, [lang, selectedDate]);

  /**
   * Set the initial selected time to the next hour if no time is selected. We do this in a useEffect to avoid hydration mismatch.
   */
  useEffect(() => {
    if (selectedTime === null) {
      const now = dayjs();
      const currentHour = now.hour();
      const currentMinute = now.minute();
      const targetHour = currentMinute > 0 ? currentHour + 1 : currentHour;
      const hour = targetHour > 23 ? 0 : targetHour;
      setSelectedTime(dayjs().hour(hour).minute(0).format('HH:mm'));
    }
  }, [selectedTime]);

  return (
    <ClassfinderContext.Provider value={value}>
      {children}
    </ClassfinderContext.Provider>
  );
}

export function useClassfinderContext() {
  const context = useContext(ClassfinderContext);
  if (context === undefined) {
    throw new Error('useClassfinder must be used within a ClassfinderProvider');
  }
  return context;
}
